import React from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import * as FS from '@fullstory/browser'

import useDidMount from '@divvy-web/hooks.usedidmount'
import Spinner from '@divvy-web/skylab.spinner'

import { useAuth } from '@sputnik/auth'
import { PAGE_NAME_STRINGS, PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'
import { setReturnFlowLocalStorage } from '@sputnik/utils/neoRedirectUtils'

import { useGetPostLoginDecisionDataLazyQuery } from './GetPostLoginDecisionData.gql'
import {
  getApplicationRedirectionRouting,
  getMatchingBdcCreditApplication,
  getRedirectableCreditApplication,
} from './utils'

const PostLoginPage = () => {
  const navigate = useNavigate()
  const { search, state } = useLocation()
  const { email, getCreditAppIdOnToken, getJwtLookupIdOnToken } = useAuth()
  const [searchParams] = useSearchParams()
  const [getPostLoginDecisionData] = useGetPostLoginDecisionDataLazyQuery()

  const sendCreditIdsFSEvent = (salesforceCreditIds?: string[]) => {
    if (FS.isInitialized() && salesforceCreditIds?.length) {
      FS.event('Auth Credit IDs', { sf_credit_id_strs: salesforceCreditIds })
    }
  }

  const handleError = (error: Error) => {
    logError(error, {
      attributes: {
        action: 'getCreditApplicant',
        message: error?.message,
        result: 'Error getting credit applicant info post login',
      },
      eventName: 'getCreditApplicantFailure',
    })

    navigate(
      {
        pathname: PATHNAME_CONSTS.DASHBOARD_PATH,
      },
      {
        replace: true,
      },
    )
  }

  useDidMount(() => {
    // If some parameter called "returnFlow" is passed, which comes somewhere
    // from the AP/AR side, we set it in localstorage such that when the user
    // exits their credit app they are taken back to there.
    const returnFlow = searchParams.get('returnFlow')
    if (returnFlow) {
      setReturnFlowLocalStorage(returnFlow)
    }

    // If a referrer is set in the search parameters, we will redirect the user there
    // immedaitely and short circuit all the logic here.
    const referrer = searchParams.get('referrer')
    if (referrer) {
      navigate(referrer, { replace: true })
      return
    }

    // We fetch what we determine is the "post login decisioning data", which boils down
    // to a little bit of data associated their associated credit applications. We do this
    // with promise chaining because otherwise we'd leave the closure.
    getPostLoginDecisionData()
      .then(({ data, error }) => {
        if (error) {
          handleError(error)
          return
        }

        const creditApplications = data?.creditApplications ?? []
        const hasSalesforceContact = data?.salesforceContactExists ?? false

        // This is a param we are sent that apparently will either contain the value
        // 'dashboard' or something else - I'm not sure what other things it can be, but
        // for this purpose all we care about is if it should stay on the dashboard or not.
        const redirectToParam = searchParams.get('redirect_to')?.toLowerCase()
        const shouldStayOnDashboardViaRedirectParam = redirectToParam === PAGE_NAME_STRINGS.PAGE_DASHBOARD

        // if the user has no credit applications, does not have a salesforce contact
        // associated with them, and is not specifically routed to the dashboard,
        // we must route them to the good fit survey.
        if (
          creditApplications.length < 1 &&
          !getCreditAppIdOnToken() &&
          !getJwtLookupIdOnToken() &&
          !hasSalesforceContact &&
          !shouldStayOnDashboardViaRedirectParam
        ) {
          navigate(
            {
              pathname: PATHNAME_CONSTS.GOOD_FIT_SURVEY_PATH,
              search,
            },
            {
              replace: true,
            },
          )
          return
        }

        // Now that we know they have credit applications, send these values to
        // fullstory to associate with the session.
        sendCreditIdsFSEvent(creditApplications.map(({ salesforceCreditId }) => salesforceCreditId))

        // These parameters we use to directly route a user to their credit application
        // via their associated BDC org ID or a directly known credit application ID.
        const creditIdParam = searchParams.get('credit_id')?.toLowerCase()
        const bdcOrgIdParam = searchParams.get('bdc_org_id')?.toLowerCase()

        // Find a credit application for the user to be automatically
        // redirected to, given the above query parameters as well as
        // the identity of the logged in user.
        const creditApplication = getRedirectableCreditApplication({
          creditApplications,
          bdcOrgIdParam,
          creditIdParam,
          currentUserEmail: email,
        })

        // If we find a redirectable credit application, and we are not explicitly
        // told to redirect to the dashboard, we will redirect the user to the
        // found application available for redirection.
        if (creditApplication && !shouldStayOnDashboardViaRedirectParam) {
          const { salesforceCreditId, recordType } = creditApplication

          logInfo('dashboardActiveCreditAppBypass', {
            attributes: {
              creditAppId: salesforceCreditId,
              isBdcOrg: !!getMatchingBdcCreditApplication({ creditApplications, bdcOrgIdParam }),
              result: `Bypassing dashboard to go to the credit application ${salesforceCreditId}`,
            },
            eventName: 'PageLoad',
          })

          // Get the routing information we need from the credit application, given the
          // record type and application version.
          const { pathname, autoRedirectIndex } = getApplicationRedirectionRouting({
            salesforceCreditId,
            recordType,
          })

          navigate(
            {
              pathname,
              search,
            },
            {
              state: {
                ...state,
                autoRedirectIndex,
                referrer: 'dashboard',
              },
            },
          )
          return
        }

        // If none of the above conditions are satisfied, we route them to the dashboard.
        navigate(
          {
            pathname: PATHNAME_CONSTS.DASHBOARD_PATH,
          },
          {
            replace: true,
          },
        )
      })
      .catch(handleError)
  })

  return <Spinner fullPage />
}

export default PostLoginPage
